/**
 * EDR data hooks — React Query wrappers around the EDR Connector API.
 * Each hook is gated by `useConnectorEnabled("edr")` so calls don't
 * fire (and 401) when the connector isn't installed.
 *
 * Adapter functions map the API DTOs onto the existing UI types in
 * `src/types/edr.ts`, so individual page components only need to swap
 * their data source — not their JSX.
 */
import { useQuery } from "@tanstack/react-query";
import { edrGet } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  AlertListDTO,
  AlertWithFramework,
  RuleListDTO,
  AIGuardBinaryListDTO,
  HealthInsightsDTO,
  QuarantineListDTO,
  QuarantineEntryDTO,
  AuditLogListDTO,
  AuditEntryDTO,
  MCPEndpointListDTO,
  MCPEndpointDTO,
  MCPProcessListDTO,
  MCPProcessDTO,
  HuntListDTO,
  HuntDTO,
  HuntResultsDTO,
  SigmaRuleListDTO,
  SigmaRuleDTO,
  PersistenceBaselineListDTO,
  PersistenceBaselineDTO,
  PersistenceSnapshotListDTO,
  PersistenceSnapshotDTO,
  CoverageResponseDTO,
  ScorecardDTO,
  McpClientsResponseDTO,
  McpToolsResponseDTO,
} from "@/types/edrApi";
import { EdrApiError } from "@/services/edrApi";
import type {
  EdrAlert,
  AlertSeverity,
  AlertStatus,
  DetectionRule,
  BinaryAuthRule,
  QuarantineItem,
  AuditEntry,
} from "@/types/edr";

// ---------- Mapping helpers ----------

const SEVERITY_MAP: Record<string, AlertSeverity> = {
  critical: "critical",
  warning: "medium",
  info: "low",
};

const STATUS_MAP: Record<string, AlertStatus> = {
  open: "new",
  dismissed: "dismissed",
  escalated: "escalated",
};

function familyOf(alertType: string): EdrAlert["family"] {
  const t = alertType.toLowerCase();
  if (t.includes("ransom")) return "ransomware";
  if (t.includes("persist") || t.includes("launch") || t.includes("btm")) return "persistence";
  if (t.includes("cred") || t.includes("keychain")) return "credential_access";
  if (t.includes("code_sign") || t.includes("invalid")) return "code_sign_tamper";
  if (t.includes("dlp")) return "dlp";
  if (t.includes("agent") || t.includes("mcp") || t.includes("ai_")) return "agentic";
  return "execution";
}

function shortDevice(deviceId: string): string {
  if (!deviceId) return "—";
  return deviceId.length > 12 ? `${deviceId.slice(0, 8)}…${deviceId.slice(-4)}` : deviceId;
}

function adaptAlert(a: AlertWithFramework): EdrAlert {
  return {
    id: a.id,
    severity: SEVERITY_MAP[a.severity] ?? "info",
    status: STATUS_MAP[a.status] ?? "new",
    ts: a.createdAt,
    deviceName: shortDevice(a.deviceId),
    deviceId: a.deviceId,
    ruleId: a.alertType,
    ruleName: a.alertType,
    mitre: a.mitreId ?? "—",
    family: familyOf(a.alertType),
    process: a.processPath ?? "—",
    summary: a.detectionReason,
    consoleUser: a.consoleUser ?? a.user ?? null,
  };
}

function adaptRule(r: RuleListDTO["rules"][number]): DetectionRule {
  return {
    id: r.ruleId,
    name: r.name,
    severity: "medium", // rules list doesn't carry severity
    mitre: r.mitreTechId ?? "—",
    family: familyOf(r.ruleId),
    description: `${(r.platforms ?? []).join(", ").toUpperCase() || "—"}${
      r.enabled === false ? " · disabled" : ""
    }`,
    controls: [],
    lastFired24h: 0,
  };
}

function adaptBinary(b: AIGuardBinaryListDTO["binaries"][number], i: number): BinaryAuthRule {
  return {
    id: `${b.canonicalName ?? "binary"}_${i}`,
    match: b.teamId ? "team-id" : "path",
    value: b.teamId ?? (b.binaryNames?.[0] ?? b.binaryContains?.[0] ?? b.canonicalName ?? "—"),
    mode: "allow",
    comment: b.canonicalName ? `${b.canonicalName} · ${b.publisher ?? "unknown publisher"}` : undefined,
    createdAt: new Date().toISOString(),
    author: "ai-guard",
  };
}

function pickArray<T>(payload: unknown, keys: string[]): T[] {
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) {
    const v = (payload as Record<string, unknown>)[key];
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

function adaptQuarantine(q: QuarantineEntryDTO, i: number): QuarantineItem {
  return {
    id: q.id ?? `q_${i}`,
    sha256: q.sha256 ?? q.hash ?? "—",
    originalPath: q.originalPath ?? q.path ?? "—",
    deviceName: q.deviceName ?? shortDevice(q.deviceId ?? ""),
    size: typeof q.size === "number" ? q.size : 0,
    capturedAt: q.capturedAt ?? q.createdAt ?? new Date().toISOString(),
    alertId: q.alertId,
  };
}

function adaptAudit(e: AuditEntryDTO, i: number): AuditEntry {
  const decision = (e.decision ?? e.action ?? "allow").toString().toLowerCase();
  const norm: AuditEntry["decision"] =
    decision.includes("dry") ? "dry-run-deny" : decision.includes("deny") ? "deny" : "allow";
  return {
    id: e.id ?? `audit_${i}`,
    ts: e.ts ?? e.createdAt ?? new Date().toISOString(),
    actor: e.actor ?? "—",
    decision: norm,
    binary: e.binary ?? e.target ?? "—",
    deviceName: e.deviceName ?? shortDevice(e.deviceId ?? ""),
    ruleId: e.ruleId,
  };
}

// ---------- Hooks ----------

const STALE_MS = 30_000;

export function useEdrAlerts(params: { limit?: number; severity?: string } = {}) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "alerts", params],
    staleTime: STALE_MS,
    queryFn: async (): Promise<EdrAlert[]> => {
      const res = await edrGet<AlertListDTO>("/api/v1/connectors/edr/alerts", {
        params: { limit: params.limit ?? 100, severity: params.severity },
      });
      return (res?.alerts ?? []).map(adaptAlert);
    },
  });
}

export function useEdrRules() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "rules"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<DetectionRule[]> => {
      const res = await edrGet<RuleListDTO>("/api/v1/connectors/edr/rules");
      return (res?.rules ?? []).map(adaptRule);
    },
  });
}

export function useEdrBinAuth() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "ai-guard", "binaries"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<BinaryAuthRule[]> => {
      const res = await edrGet<AIGuardBinaryListDTO>("/api/v1/connectors/edr/ai-guard/binaries");
      return (res?.binaries ?? []).map(adaptBinary);
    },
  });
}

export function useEdrQuarantine() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "quarantine"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<QuarantineItem[]> => {
      const res = await edrGet<QuarantineListDTO>("/api/v1/connectors/edr/quarantine");
      const rows = pickArray<QuarantineEntryDTO>(res, ["entries", "quarantine", "data"]);
      return rows.map(adaptQuarantine);
    },
  });
}

export function useEdrHealthInsights() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "health-insights"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<HealthInsightsDTO> => {
      const res = await edrGet<HealthInsightsDTO>(
        "/api/v1/connectors/edr/reports/health-insights",
      );
      return res ?? {};
    },
  });
}

export function useEdrAudit() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "audit"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<AuditEntry[]> => {
      const res = await edrGet<AuditLogListDTO>("/api/v1/connectors/edr/admin/audit-log");
      const rows = pickArray<AuditEntryDTO>(res, ["entries", "audit", "data"]);
      return rows.map(adaptAudit);
    },
  });
}

export function useEdrMcpEndpoints() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "mcp", "endpoints"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<MCPEndpointDTO[]> => {
      const res = await edrGet<MCPEndpointListDTO>("/api/v1/connectors/edr/mcp/endpoints");
      return pickArray<MCPEndpointDTO>(res, ["endpoints", "data"]);
    },
  });
}

export function useEdrMcpProcesses() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "mcp", "processes"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<MCPProcessDTO[]> => {
      const res = await edrGet<MCPProcessListDTO>("/api/v1/connectors/edr/mcp/processes");
      return pickArray<MCPProcessDTO>(res, ["processes", "data"]);
    },
  });
}

// ---------- Pass 2 read surfaces ----------

export function useEdrHunts() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "hunts"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<HuntDTO[]> => {
      const res = await edrGet<HuntListDTO>("/api/v1/connectors/edr/admin/hunts");
      return pickArray<HuntDTO>(res, ["hunts", "data"]);
    },
  });
}

export function useEdrHuntResults(huntId: string | undefined) {
  const connectorEnabled = useConnectorEnabled("edr");
  return useQuery({
    enabled: connectorEnabled && !!huntId,
    queryKey: ["edr", "hunts", huntId, "results"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<HuntResultsDTO> => {
      const res = await edrGet<HuntResultsDTO>(
        `/api/v1/connectors/edr/admin/hunts/${huntId}/results`,
      );
      return res ?? {};
    },
  });
}

export function useEdrSigmaRules() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "sigma", "rules"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<SigmaRuleDTO[]> => {
      // Try public listing first, fall back to admin listing (covers both
      // demo postures — public read enabled, admin-only read).
      try {
        const res = await edrGet<SigmaRuleListDTO>("/api/v1/connectors/edr/sigma/rules");
        const rows = pickArray<SigmaRuleDTO>(res, ["rules", "data"]);
        if (rows.length > 0) return rows;
      } catch {
        // fall through
      }
      const admin = await edrGet<SigmaRuleListDTO>(
        "/api/v1/connectors/edr/admin/sigma-rules",
      );
      return pickArray<SigmaRuleDTO>(admin, ["rules", "data"]);
    },
  });
}

export function useEdrPersistenceBaselines() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "persistence", "baselines"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<PersistenceBaselineDTO[]> => {
      const res = await edrGet<PersistenceBaselineListDTO>(
        "/api/v1/connectors/edr/persistence/baselines",
      );
      return pickArray<PersistenceBaselineDTO>(res, ["baselines", "data"]);
    },
  });
}

export function useEdrPersistenceSnapshots() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "persistence", "snapshots"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<PersistenceSnapshotDTO[]> => {
      const res = await edrGet<PersistenceSnapshotListDTO>(
        "/api/v1/connectors/edr/persistence/snapshots",
      );
      return pickArray<PersistenceSnapshotDTO>(res, ["snapshots", "data"]);
    },
  });
}

export function useEdrMitreCoverage(technique: string | undefined) {
  const connectorEnabled = useConnectorEnabled("edr");
  const valid = !!technique && technique !== "—";
  return useQuery({
    enabled: connectorEnabled && valid,
    queryKey: ["edr", "research", "coverage", technique],
    staleTime: STALE_MS,
    queryFn: async (): Promise<CoverageResponseDTO> => {
      const res = await edrGet<CoverageResponseDTO>(
        "/api/v1/connectors/edr/research/coverage",
        { params: { technique } },
      );
      const coverage =
        res?.coverage ?? res?.rules ?? (Array.isArray(res?.data) ? res.data : []) ?? [];
      return { ...res, coverage };
    },
  });
}

// ---------- Detection catalog (research/coverage, tenant-scoped) ----------

interface MitreMappingDTO {
  tactic?: string;
  tacticId?: string;
  tacticName?: string;
  technique?: string;
  techniqueId?: string;
  techniqueName?: string;
  subTechnique?: string;
  subTechniqueId?: string;
  id?: string;
}

interface CoverageDetectionDTO {
  id?: string;
  ruleId?: string;
  name?: string;
  title?: string;
  description?: string;
  severity?: string;
  family?: string;
  platforms?: string[];
  mitre?: MitreMappingDTO | MitreMappingDTO[] | string | null;
  mitreTactic?: string;
  mitreTechId?: string;
  mitreTechniqueId?: string;
  tactic?: string;
  technique?: string;
  d3fend?: string[];
  art?: string[];
  controls?: string[] | Array<{ framework?: string; controlId?: string }>;
  recentAlertCount?: number;
  enabled?: boolean;
  [k: string]: unknown;
}

interface CoverageCatalogResponse {
  detections?: CoverageDetectionDTO[];
  rules?: CoverageDetectionDTO[];
  data?: CoverageDetectionDTO[];
  warning?: string;
  [k: string]: unknown;
}

const CATALOG_SEVERITY: Record<string, AlertSeverity> = {
  critical: "critical",
  high: "high",
  medium: "medium",
  warning: "medium",
  low: "low",
  info: "info",
};

function adaptControls(
  c: CoverageDetectionDTO["controls"],
): string[] {
  if (!c) return [];
  if (Array.isArray(c) && typeof c[0] === "string") return c as string[];
  return (c as Array<{ framework?: string; controlId?: string }>)
    .map((x) => (x.framework ? `${x.framework}:${x.controlId ?? ""}` : x.controlId ?? ""))
    .filter(Boolean);
}

function pickFirstMitre(
  mitre: CoverageDetectionDTO["mitre"],
): MitreMappingDTO | null {
  if (!mitre) return null;
  if (typeof mitre === "string") return { techniqueId: mitre };
  if (Array.isArray(mitre)) return mitre[0] ?? null;
  return mitre;
}

function allMitreMappings(
  d: CoverageDetectionDTO,
): Array<{ tactic?: string; techniqueId: string }> {
  const raw = d.mitre;
  const arr: MitreMappingDTO[] = [];
  if (typeof raw === "string") arr.push({ techniqueId: raw });
  else if (Array.isArray(raw)) arr.push(...raw);
  else if (raw && typeof raw === "object") arr.push(raw);

  // Fallback to top-level fields if mitre is empty/missing.
  if (arr.length === 0) {
    const tid = d.mitreTechId ?? d.mitreTechniqueId ?? d.technique;
    const t = d.mitreTactic ?? d.tactic;
    if (tid || t) arr.push({ techniqueId: tid ?? "", tactic: t });
  }

  return arr
    .map((m) => {
      const techniqueId =
        m.subTechniqueId ?? m.techniqueId ?? m.technique ?? m.id ?? "";
      const tactic = m.tactic ?? m.tacticName ?? m.tacticId ?? undefined;
      return { techniqueId, tactic };
    })
    .filter((m) => m.techniqueId);
}

function adaptCatalogDetection(d: CoverageDetectionDTO): DetectionRule {
  const id = d.id ?? d.ruleId ?? d.name ?? "detection";
  const mappings = allMitreMappings(d);
  const first = mappings[0];
  const techniqueId = first?.techniqueId ?? "—";
  const tactic =
    first?.tactic ?? d.mitreTactic ?? d.tactic ?? undefined;
  const platforms = d.platforms ?? [];
  return {
    id,
    name: d.name ?? d.title ?? id,
    severity: CATALOG_SEVERITY[(d.severity ?? "").toLowerCase()] ?? "medium",
    mitre: techniqueId || "—",
    tactic,
    mitreMappings: mappings,
    family: (d.family as DetectionRule["family"]) ?? familyOf(id),
    description:
      d.description ??
      `${platforms.join(", ").toUpperCase() || "—"}${d.enabled === false ? " · disabled" : ""}`,
    controls: adaptControls(d.controls),
    d3fend: d.d3fend ?? [],
    art: d.art ?? [],
    platforms,
    recentAlertCount: typeof d.recentAlertCount === "number" ? d.recentAlertCount : 0,
    lastFired24h: typeof d.recentAlertCount === "number" ? d.recentAlertCount : 0,
  };
}



export function useEdrCoverageCatalog() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "research", "coverage", "catalog"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<{ detections: DetectionRule[]; warning?: string }> => {
      const res = await edrGet<CoverageCatalogResponse>(
        "/api/v1/connectors/edr/research/coverage",
      );
      const rows = pickArray<CoverageDetectionDTO>(res, ["detections", "rules", "data"]);
      return {
        detections: rows.map(adaptCatalogDetection),
        warning: res?.warning,
      };
    },
  });
}

/**
 * Coverage scorecard — single endpoint that powers the Posture hero
 * strip and the Coverage scorecard page. P2 endpoint: 404 is treated
 * as "no scorecard available yet" so the UI degrades gracefully.
 */
export function useEdrScorecard(sinceHours = 168) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "research", "scorecard", sinceHours],
    staleTime: STALE_MS,
    queryFn: async (): Promise<ScorecardDTO | null> => {
      try {
        const res = await edrGet<ScorecardDTO>(
          "/api/v1/connectors/edr/research/scorecard",
          { params: { sinceHours } },
        );
        return res ?? null;
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * Active MCP clients that have OAuth'd into this tenant. P2 endpoint —
 * 404 is treated as "no clients yet" so the page renders an empty
 * state rather than a destructive banner.
 */
export function useMcpClients() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "admin", "mcp", "clients"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<McpClientsResponseDTO | null> => {
      try {
        return (await edrGet<McpClientsResponseDTO>(
          "/api/v1/connectors/edr/admin/mcp/clients",
        )) ?? null;
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/** Catalog of MCP tools exposed by Smplify. Same 404 tolerance as clients. */
export function useMcpTools() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "admin", "mcp", "tools"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<McpToolsResponseDTO | null> => {
      try {
        return (await edrGet<McpToolsResponseDTO>(
          "/api/v1/connectors/edr/admin/mcp/tools",
        )) ?? null;
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}



